import { AxiosError } from 'axios';

// FastAPI returns validation errors as { detail: [{ loc, msg, type }, ...] } and
// HTTPException errors as { detail: "message" }. These helpers normalize either
// shape into safe, renderable strings so we never pass an object/array to React
// (which would crash with "Objects are not valid as a React child").

type FastApiValidationItem = {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
};

const isValidationItemArray = (value: unknown): value is FastApiValidationItem[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'object' && v !== null);

/**
 * Optional dictionary that maps the exact English API message to a localized
 * one. Pydantic v2 prefixes ValueError messages with "Value error, ", so we
 * strip that before looking up (and fall back to the raw message).
 */
export type ApiMessageDict = Record<string, string>;

const localize = (msg: string, dict?: ApiMessageDict): string => {
  if (!dict || !msg) return msg;
  const stripped = msg.replace(/^Value error,\s*/i, '');
  return dict[stripped] || dict[msg] || msg;
};

/** Extract a single human-readable error message from any API/axios error. */
export const getApiErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong',
  dict?: ApiMessageDict,
): string => {
  const data = (error as AxiosError)?.response?.data as any;

  if (!data) {
    const msg = (error as Error)?.message;
    return typeof msg === 'string' && msg ? msg : fallback;
  }

  // Custom { message: "..." } shape
  if (typeof data.message === 'string' && data.message) {
    return localize(data.message, dict);
  }

  const detail = data.detail;
  if (typeof detail === 'string' && detail) {
    return localize(detail, dict);
  }
  if (isValidationItemArray(detail)) {
    const msgs = detail
      .map((d) => d.msg)
      .filter((m): m is string => typeof m === 'string' && !!m)
      .map((m) => localize(m, dict));
    if (msgs.length) return msgs.join(', ');
  }

  return fallback;
};

/**
 * Map a FastAPI 422 validation error to per-field messages keyed by field name,
 * e.g. { email: ["Invalid email address"] }. Falls back to a `general` key.
 */
export const getApiFieldErrors = (
  error: unknown,
  dict?: ApiMessageDict,
): { [key: string]: string[] } => {
  const data = (error as AxiosError)?.response?.data as any;
  const detail = data?.detail;

  if (isValidationItemArray(detail)) {
    const fieldErrors: { [key: string]: string[] } = {};
    for (const item of detail) {
      // loc looks like ["body", "email"] -> use the last string segment as the field.
      const field =
        Array.isArray(item.loc) && item.loc.length
          ? String(item.loc[item.loc.length - 1])
          : 'general';
      const msg = localize(item.msg || 'Invalid value', dict);
      (fieldErrors[field] ||= []).push(msg);
    }
    return fieldErrors;
  }

  return { general: [getApiErrorMessage(error, undefined, dict)] };
};
